import { emptyFragment } from '@execonline-inc/execonline-ui';
import { reaction } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';
import { useEffect } from 'react';
import { Footer } from '../../../../components/Footer';
import { intercomContextStore } from '../../../../IntercomContextStore';
import { discoveryStore } from '../../../../DiscoveryStore';
import useDiscoveryStore from '../../../../DiscoveryStore/UseDiscoveryStore';
import { localPublicUrl } from '../../../MobileImageBackground/Types';
import EnrollmentStore from '../Enrollment/Store';
import { PreviewModeContext } from '../PreviewMode/PreviewModeContext';
import Promotables from '../Promotables';
import { RegisterResource } from '../RegisterResourceStore/Types';
import EnrollableExperience from './EnrollableExperience';
import ExperienceFiltersSelectionView from './ExperienceFiltersSelectionView';
import ExperienceSelectionView from './ExperienceSelectionView';
import LicenseOfferPrompt from '../../Common/LicensePurchase/Views/LicenseOfferPrompt';
import ExperiencesStore from './Experiences/ExperiencesStore';
import { ExperienceFiltersProvider } from './Filters/FiltersContext';
import Header from './Header';
import Notification from './Notification';

interface Props {
  invitationUuid: string;
  enrollmentStore: EnrollmentStore;
  experiencesStore: ExperiencesStore;
  registerResource: RegisterResource;
}

export function getBackgroundImage(bgImage: string = 'bg-chevrons.svg'): React.CSSProperties {
  return {
    backgroundImage: `url("${localPublicUrl(bgImage)}")`,
    backgroundPositionY: '-80px',
  };
}

function ExperienceSelection({
  invitationUuid,
  experiencesStore,
  enrollmentStore,
  registerResource,
}: Props) {
  useDiscoveryStore();

  useEffect(() => {
    // Use MobX reaction to watch for when discoveryStore becomes loaded
    // This avoids putting MobX observables in React dependency arrays
    const dispose = reaction(
      () => ({
        isLoaded: discoveryStore.isLoaded,
        collections: discoveryStore.learningCollections,
      }),
      ({ isLoaded, collections }) => {
        if (!isLoaded) {
          return;
        }

        // Find the collection by UUID and set it in the context store
        const collection = collections.find(
          (collection) => collection.payload.uuid === invitationUuid,
        );

        if (collection) {
          intercomContextStore.setCurrentCollection(
            collection.payload.id.toString(),
            collection.payload.name.text,
          );
        } else {
          intercomContextStore.clearCurrentCollection();
        }

        // Clear program when navigating to collection view
        intercomContextStore.clearCurrentProgram();
      },
      { fireImmediately: true },
    );

    // Cleanup on unmount
    return () => {
      dispose();
      intercomContextStore.clearAll();
    };
  }, [invitationUuid]);

  return (
    <div
      style={getBackgroundImage()}
      className="bg-chevrons flex flex-grow flex-col bg-[length:100%] bg-repeat-y"
    >
      <Header />
      <ExperienceFiltersProvider>
        <ExperienceFiltersSelectionView experiencesStore={experiencesStore} />
        <Notification notification={enrollmentStore.notification} />
        <div className="flex flex-1 flex-col gap-y-8">
          <LicenseOfferPrompt />
          <Promotables
            invitationUuid={invitationUuid}
            registerResource={registerResource}
            enrollmentStore={enrollmentStore}
            experiencesStore={experiencesStore}
          />
          <ExperienceSelectionView
            invitationUuid={invitationUuid}
            enrollmentStore={enrollmentStore}
            experiencesStore={experiencesStore}
          />
          <EnrollableExperience
            invitationUuid={invitationUuid}
            notEnrollableAlert={experiencesStore.notEnrollableAlert}
            enrollmentStore={enrollmentStore}
          />
        </div>
      </ExperienceFiltersProvider>
      <PreviewModeContext.Consumer>
        {(previewMode) => {
          switch (previewMode) {
            case 'default':
              return <Footer />;
            case 'preview':
              return emptyFragment();
          }
        }}
      </PreviewModeContext.Consumer>
    </div>
  );
}

export default observer(ExperienceSelection);
